import { Link } from 'react-router-dom';
import Seo from '@/components/Seo';
import { SITE } from '@/lib/site';
import { getSeo } from '@/lib/seo';

export default function About() {
  return (
    <>
      <Seo
        path="/about"
        fullTitle={getSeo('about').title}
        description={getSeo('about').description}
        ogImage={getSeo('about').og_image ?? undefined}
      />
      <section className="border-b border-border">
        <div className="mx-auto max-w-page px-5 py-20 sm:px-8 sm:py-24">
          <div className="max-w-3xl">
            <p className="text-sm font-medium uppercase tracking-wider text-accent">
              About
            </p>
            <p className="mt-6 text-lg text-fg-muted">
              {SITE.name} is a small team of predominantly British developers with collectively decades of web design, development, and hosting experience.
            </p>
          </div>
        </div>
      </section>

      <section className="border-b border-border">
        <div className="mx-auto grid max-w-page gap-12 px-5 py-20 sm:px-8 sm:py-24 md:grid-cols-2">
          <div>
            <h2 className="text-3xl">Why we exist</h2>
            <div className="mt-5 space-y-4 text-fg-muted">
              <p>
                Most small businesses get one of two things: a cheap drag-and-drop
                template that breaks in odd ways, or an agency build that costs five
                figures and ships a year late. Neither is good for the client.
              </p>
              <p>
                We sit in the middle. We're a small team who are passionate about building software. We don't want to overcharge, and we don't want to give little effort.
              </p>
            </div>
          </div>
          <div>
            <h2 className="text-3xl">How we work</h2>
            <ol className="mt-5 space-y-5 text-sm text-fg-muted">
              <Step n={1} title="A short conversation">
                You tell us what you do, who it's for, and roughly what you'd like
                to spend. We tell you whether we're a good fit and roughly what's
                involved.
              </Step>
              <Step n={2} title="A fixed quote">
                Once we understand your needs, we'll get back to you with a quote.
              </Step>
              <Step n={3} title="Build and review">
                We design and build the website. We run a copy for you to test locally. You give us feedback, and we adjust until you're happy.
              </Step>
              <Step n={4} title="Launch and look after">
                Depending on whether you've bought just the development or the hosting package as well, we'll either hand over the website's code and assets or we launch it ourselves and host it for you. DNS, email, and other techical stuff we handle ourselves. It's stress-free for you.
              </Step>
            </ol>
          </div>
        </div>
      </section>

      <section className="border-b border-border">
        <div className="mx-auto max-w-page px-5 py-20 sm:px-8 sm:py-24">
          <h2 className="text-3xl">What "properly built" means to us</h2>
          <div className="mt-8 grid gap-6 md:grid-cols-3">
            <Card title="Fast by default">
              The code is optimised and fast. We don't overbear your customers and users with bloated software that takes minutes to load.
            </Card>
            <Card title="Accessible by default">
              The websites are accessible from almost any device with proper screen sizes, and optionally we can provide accessibility features for those with disabilities.
            </Card>
            <Card title="Findable by default">
              We optimise your site to be easily found by search engines. Note that this does not include free advertising.
            </Card>
          </div>
        </div>
      </section>

      <section>
        <div className="mx-auto max-w-page px-5 py-20 sm:px-8 sm:py-24">
          <div className="flex flex-col items-start gap-6 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-3xl">Want to talk?</h2>
              <p className="mt-2 text-fg-muted">
                Drop us a line at{' '}
                <a className="link" href={`mailto:${SITE.email}`}>
                  {SITE.email}
                </a>{' '}
                or give us a call.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link to="/contact" className="btn-primary px-5 py-3 text-base">
                Start a project
              </Link>
              <a href={SITE.phoneHref} className="btn-ghost px-5 py-3 text-base">
                {SITE.phone}
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function Step({
  n,
  title,
  children,
}: {
  n: number;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <li className="flex gap-4">
      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full border border-accent/40 text-sm text-accent">
        {n}
      </span>
      <div>
        <p className="text-base font-medium text-fg-base">{title}</p>
        <p className="mt-1 leading-relaxed">{children}</p>
      </div>
    </li>
  );
}

function Card({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <article className="card p-6">
      <h3 className="text-xl">{title}</h3>
      <p className="mt-3 text-sm leading-relaxed text-fg-muted">{children}</p>
    </article>
  );
}
